import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { clipboardService, adminService } from '../services/api';
import LinkPreview from './LinkPreview';
import HorizontalVoteButtons from './HorizontalVoteButtons';
import CommentSection from './CommentSection';
import ImageModal from './ImageModal';
import useInfiniteScroll from '../hooks/useInfiniteScroll';
   import {
    Search,
    Filter,
    RefreshCw,
    Loader2,
    ChevronLeft,
    ChevronRight,
    Hash,
    Eye,
    Clock,
    Users,
    FileText,
    MoreHorizontal,
    Plus,
    FolderOpen,
    Sparkles,
    Code,
    Palette,
    Music,
    Camera,
    BookOpen,
    Gamepad2,
    Heart,
    Star,
    Zap,
    Globe,
    Settings,
    UserPlus,
    ArrowLeft,
    TrendingUp,
    LogOut,
    TrendingDown,
    Tag,
    Layers,
    MessageCircle,
    Award,
    Share2,
    Bookmark,
    Grid,
    List,
    ChevronUp,
    Calendar,
    MapPin,
    ZoomIn
  } from 'lucide-react';
import toast from 'react-hot-toast';

const PublicCategoryPage = () => {
  const { categoryName } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    content_type: '',
    sort_by: 'latest'
  });
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_items: 0,
    items_per_page: 20,
  });
  const [hasMore, setHasMore] = useState(true);

  // Category editing state
  const [editingItem, setEditingItem] = useState(null);
  const [editCategory, setEditCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  
  // Comments expansion state
  const [commentsExpanded, setCommentsExpanded] = useState({});
  const [commentCounts, setCommentCounts] = useState({});
  const [commentData, setCommentData] = useState({});

  // Image modal state
  const [imageModal, setImageModal] = useState({
    isOpen: false,
    imageSrc: '',
    imageAlt: ''
  });

  // Pre-defined icon mapping for reliability
  const categoryIconMap = {
    'tech': Sparkles,
    'tips': Sparkles,
    'help': Sparkles,
    'web': Code,
    'dev': Code,
    'code': Code,
    'development': Code,
    'design': Palette,
    'art': Palette,
    'creative': Palette,
    'productivity': Zap,
    'work': Zap,
    'business': Zap,
    'news': TrendingUp,
    'update': TrendingUp,
    'announcement': TrendingUp,
    'music': Music,
    'audio': Music,
    'sound': Music,
    'photo': Camera,
    'image': Camera,
    'camera': Camera,
    'book': BookOpen,
    'read': BookOpen,
    'article': BookOpen,
    'game': Gamepad2,
    'play': Gamepad2,
    'fun': Gamepad2,
    'health': Heart,
    'fitness': Heart,
    'wellness': Heart,
    'star': Star,
    'favorite': Star,
    'best': Star,
    'world': Globe,
    'global': Globe,
    'international': Globe,
    'settings': Settings,
    'config': Settings,
    'setup': Settings,
    'user': Users,
    'people': Users,
    'community': Users,
    'time': Clock,
    'schedule': Clock,
    'calendar': Clock,
    'location': MapPin,
    'place': MapPin,
    'map': MapPin,
    'tag': Tag,
    'label': Tag,
    'category': Tag,
    'layer': Layers,
    'stack': Layers,
    'group': Layers,
    'trend': TrendingUp,
    'popular': TrendingUp,
    'viral': TrendingUp,
    'down': TrendingDown,
    'decrease': TrendingDown,
    'fall': TrendingDown,
    'award': Award,
    'achievement': Award,
    'recognition': Award,
    'message': MessageCircle,
    'chat': MessageCircle,
    'communication': MessageCircle,
    'plus': Plus,
    'add': Plus,
    'new': Plus,
    'more': MoreHorizontal,
    'extra': MoreHorizontal,
    'additional': MoreHorizontal,
    'share': Share2,
    'social': Share2,
    'network': Share2,
    'bookmark': Bookmark,
    'save': Bookmark,
    'eye': Eye,
    'view': Eye,
    'watch': Eye,
    'grid': Grid,
    'layout': Grid,
    'dashboard': Grid,
    'list': List,
    'menu': List,
    'items': List,
    'chevron': ChevronUp,
    'arrow': ChevronUp,
    'direction': ChevronUp,
    'hash': Hash,
    'number': Hash,
    'pound': Hash,
    'userplus': UserPlus,
    'invite': UserPlus,
    'join': UserPlus
  };

  const getCategoryIcon = (categoryName) => {
    if (!categoryName) return FolderOpen;
    
    const name = categoryName.toLowerCase();
    
    // Check for exact matches first
    for (const [keyword, icon] of Object.entries(categoryIconMap)) {
      if (name.includes(keyword)) {
        return icon;
      }
    }
    
    // Default fallback - always return a valid icon
    return FolderOpen;
  };

  const getCategoryColor = (categoryId) => {
    const colors = [
      'bg-blue-500', 'bg-green-500', 'bg-purple-500', 
      'bg-orange-500', 'bg-red-500', 'bg-pink-500',
      'bg-indigo-500', 'bg-teal-500', 'bg-yellow-500'
    ];
    const index = categoryId.toString().length % colors.length;
    return colors[index];
  };

  const fetchCategoryItems = async (page = 1, search = searchTerm, filterParams = filters, append = false) => {
    try {
      // First, get the category info to find the ID
      const categoriesResponse = await clipboardService.getCategories();
      const categoryData = categoriesResponse.categories?.find(cat => 
        cat.name.toLowerCase() === decodeURIComponent(categoryName).toLowerCase()
      );
      
      if (!categoryData) {
        toast.error('Category not found');
        navigate('/public');
        return;
      }

      // Set category info from the data we already fetched
      setCategory({
        id: categoryData.id,
        name: categoryData.name,
        description: categoryData.description,
        icon: getCategoryIcon(categoryData.name),
        color: categoryData.color || getCategoryColor(categoryData.id),
        member_count: categoryData.member_count || 0,
        item_count: categoryData.item_count || 0,
        created_by: categoryData.created_by || 'Unknown',
        created_at: categoryData.created_at
      });

      const params = {
        page,
        limit: pagination.items_per_page,
      };
      
      // Only include content_type if it has a value
      if (filterParams.content_type && filterParams.content_type.trim() !== '') {
        params.content_type = filterParams.content_type;
      }
      
      if (search) {
        params.search = search;
      }

             // Get items for this specific category using the dedicated endpoint
       const response = await clipboardService.getCategoryItems(categoryData.id, params);
      
      // Use the items directly from the API response
      const items = response.items || [];
      
      // Update items - append for infinite scroll, replace for new searches/filters
      setItems(prevItems => {
        if (append) {
          return [...prevItems, ...items];
        }
        return items;
      });
      
      setPagination({
        ...response.pagination,
        total_items: response.pagination?.total_items || items.length,
        total_pages: response.pagination?.total_pages || Math.ceil(items.length / pagination.items_per_page)
      });
      
      // Update hasMore based on pagination
      setHasMore(page < (response.pagination?.total_pages || Math.ceil(items.length / pagination.items_per_page)));
    } catch (error) {
      console.error('Error fetching category items:', error);
      toast.error('Failed to load category items');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };



  useEffect(() => {
    if (categoryName) {
      fetchCategoryItems();
      fetchCategories(); // Fetch categories for editing
    }
  }, [categoryName]);

    // Infinite scroll callback
  const loadMoreItems = useCallback(() => {
    if (!loading && hasMore) {
      const nextPage = pagination.current_page + 1;
      fetchCategoryItems(nextPage, searchTerm, filters, true);
    }
  }, [loading, hasMore, pagination.current_page, searchTerm, filters, fetchCategoryItems]);

  // Use infinite scroll hook
  const lastElementRef = useInfiniteScroll(loadMoreItems, hasMore, loading);

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, current_page: 1 }));
    setHasMore(true);
    fetchCategoryItems(1, searchTerm, filters, false);
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, current_page: 1 }));
    setHasMore(true);
    fetchCategoryItems(1, searchTerm, newFilters, false);
  };

  const handlePageChange = (page) => {
    fetchCategoryItems(page, searchTerm, filters, false);
  };

  const handleRefresh = () => {
    setRefreshing(true);
    setPagination(prev => ({ ...prev, current_page: 1 }));
    setHasMore(true);
    fetchCategoryItems(1, searchTerm, filters, false);
  };

     const handleVoteChange = (itemId, action, newVoteCount) => {
    // Update the item's vote count in local state
    setItems(prevItems => 
      prevItems.map(item => 
        item.id === itemId 
          ? { ...item, vote_count: newVoteCount }
          : item
      )
    );
  };

  // Category editing functions
  const handleEditCategory = (item) => {
    setEditingItem(item.id);
    setEditCategory(item.category_id || item.category || '');
  };

  const handleSaveCategory = async (itemId) => {
    try {
      await adminService.updateItemCategory(itemId, editCategory);
      
      // Update local state
      setItems(prevItems => 
        prevItems.map(item => 
          item.id === itemId 
            ? { ...item, category_id: editCategory }
            : item
        )
      );
      
      setEditingItem(null);
      setEditCategory('');
      toast.success('Category updated successfully');
    } catch (error) {
      console.error('Error updating category:', error);
      toast.error('Failed to update category');
    }
  };

  const handleCancelEdit = () => {
    setEditingItem(null);
    setEditCategory('');
  };

  // Fetch categories for editing
  const fetchCategories = async () => {
    try {
      setCategoriesLoading(true);
      const response = await clipboardService.getCategories();
      setCategories(response.categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setCategoriesLoading(false);
    }
  };

  // Comment handling
  const handleCommentCountUpdate = useCallback((itemId, count, comments) => {
    setCommentCounts(prev => ({ ...prev, [itemId]: count }));
    setCommentData(prev => ({ ...prev, [itemId]: comments }));
  }, []);

  const handleImageClick = (imageSrc, imageAlt) => {
    setImageModal({
      isOpen: true,
      imageSrc,
      imageAlt
    });
  };

  const handleCloseImageModal = () => {
    setImageModal({
      isOpen: false,
      imageSrc: '',
      imageAlt: ''
    });
  };

  const getContentTypeColor = (contentType) => {
    const colorMap = {
      text: 'bg-blue-100 text-blue-800',
      html: 'bg-green-100 text-green-800',
      url: 'bg-purple-100 text-purple-800',
      image: 'bg-pink-100 text-pink-800',
      code: 'bg-orange-100 text-orange-800',
      document: 'bg-indigo-100 text-indigo-800'
    };
    return colorMap[contentType] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-indigo-600" />
            <p className="text-gray-600">Loading category...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!category) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <FolderOpen className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Category Not Found</h2>
            <p className="text-gray-600 mb-4">The category you're looking for doesn't exist.</p>
            <Link 
              to="/public" 
              className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Public Page
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
             {/* Header */}
       <header className="bg-white shadow-sm border-b border-gray-200">
         <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
           <div className="flex justify-between items-center py-4">
             <div>
               <Link to="/" className="flex flex-col items-start hover:opacity-80 transition-opacity">
                 <img src="/big-clipboard-text-logo.jpg" alt="Big Clipboard Logo" className="h-10 w-auto" />
                 <span className="text-sm text-primary-700 font-medium mt-1">Copy Once. Use Anywhere.</span>
               </Link>
             </div>
             <div className="flex items-center space-x-4">
               <Link to="/dashboard" className="btn-secondary flex items-center">
                 <FileText className="h-4 w-4 mr-2" />
                 My Clipboard
               </Link>
               {(user?.is_admin === true || user?.is_admin === "true" || user?.is_admin === 1) && (
                 <Link to="/admin" className="bg-red-100 hover:bg-red-200 text-red-700 border-red-300 px-4 py-2 rounded-lg flex items-center transition-colors">
                   <Settings className="h-4 w-4 mr-2" />
                   Admin
                 </Link>
               )}
               <button
                 onClick={() => window.location.href = '/logout'}
                 className="btn-secondary flex items-center"
               >
                 <LogOut className="h-4 w-4 mr-2" />
                 Logout
               </button>
             </div>
           </div>
         </div>
       </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Category Header */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-4">
              <div className={`w-12 h-12 ${category.color} rounded-xl flex items-center justify-center`}>
                {(() => {
                  const IconComponent = getCategoryIcon(category.name);
                  return <IconComponent className="w-6 h-6 text-white" />;
                })()}
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{category.name}</h1>
                <p className="text-gray-600 mt-1">{category.description}</p>
                <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                  <span className="flex items-center space-x-1">
                    <Users className="w-4 h-4" />
                    <span>{category.member_count} members</span>
                  </span>
                  <span className="flex items-center space-x-1">
                    <FileText className="w-4 h-4" />
                    <span>{category.item_count} items</span>
                  </span>
                  <span>Created by {category.created_by}</span>
                </div>
              </div>
            </div>
            <div className="flex space-x-2">
              <Link
                to="/public"
                className="flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Back to All Categories</span>
              </Link>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search content..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>
            
            <div className="flex gap-3">
              <select
                value={filters.sort_by}
                onChange={(e) => handleFilterChange('sort_by', e.target.value)}
                className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="latest">Latest</option>
                <option value="popular">Most Popular</option>
                <option value="trending">Trending</option>
              </select>
              
              <button
                type="submit"
                className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
              >
                Search
              </button>
            </div>
          </form>
        </div>

        {/* Content Grid */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-gray-900">
              {category.name} Content
              <span className="text-gray-500 font-normal ml-2">({items.length} items)</span>
            </h3>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </button>
          </div>

          {items.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-gray-400 mb-4">
                <FileText className="h-12 w-12 mx-auto" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No content found</h3>
              <p className="text-gray-600">
                {searchTerm || Object.values(filters).some(f => f !== '')
                  ? 'Try adjusting your search or filters'
                  : 'This category is empty. Be the first to share something!'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {items.map((item, index) => (
                <div 
                  key={item.id} 
                  ref={index === items.length - 1 ? lastElementRef : null}
                  className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-300"
                >
                  {/* Header */}
                  <div className="p-4 border-b border-gray-100">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getContentTypeColor(item.content_type)}`}>
                          {item.content_type?.toUpperCase() || 'TEXT'}
                        </span>
                                                 {/* Category Badge */}
                         {(item.category_name || item.category || item.group) && (
                           <div className="flex items-center space-x-2">
                             <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                               {item.category_name || item.category || item.group}
                             </span>
                             {/* Edit button for item owner */}
                             {user && item.user_id === user.id && (
                               <button
                                 onClick={() => handleEditCategory(item)}
                                 className="p-1 text-gray-400 hover:text-indigo-600 transition-colors"
                                 title="Edit category"
                               >
                                 <Settings className="w-3 h-3" />
                               </button>
                             )}
                           </div>
                         )}
                      </div>
                      <div className="flex items-center">
                        <HorizontalVoteButtons
                          itemId={item.id}
                          initialVotes={item.votes || item.vote_count || 0}
                          onVoteChange={handleVoteChange}
                        />
                      </div>
                    </div>
                    
                    {/* Author Info */}
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full flex items-center justify-center">
                        <span className="text-white font-semibold text-sm">
                          {item.author_name ? item.author_name.charAt(0).toUpperCase() : 'U'}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">
                          {item.author_name || 'Anonymous'}
                        </div>
                        <div className="flex items-center space-x-2 text-xs text-gray-500">
                          <Clock className="w-3 h-3" />
                          <span>{new Date(item.created_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="p-4">
                    {item.title && (
                      <h3 className="font-semibold text-gray-900 mb-3 line-clamp-2">
                        {item.title}
                      </h3>
                    )}
                    
                    {/* Source URL */}
                    {item.source_url && (
                      <div className="mb-3">
                        <a
                          href={item.source_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-indigo-600 hover:text-indigo-800 underline break-all"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {item.source_url}
                        </a>
                      </div>
                    )}
                    
                    <div className="text-sm text-gray-700 line-clamp-3">
                      {item.content_type === 'html' ? (
                        <div className="space-y-2">
                          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 min-h-[200px]">
                            <div 
                              className="html-content"
                              dangerouslySetInnerHTML={{ __html: item.content }}
                            />
                          </div>
                          <p className="text-xs text-gray-500 text-center">
                            HTML Content
                          </p>
                        </div>
                      ) : item.content_type === 'image' ? (
                        <div className="space-y-3">
                          <div className="relative group cursor-pointer">
                            <img 
                              src={item.content} 
                              alt={item.title || 'Shared image'}
                              className="w-full h-64 object-contain rounded-lg border border-gray-200 bg-gray-50 group-hover:border-indigo-300 transition-colors"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'block';
                              }}
                              onClick={() => handleImageClick(item.content, item.title || 'Shared image')}
                            />
                            {/* Overlay with zoom icon */}
                            <div 
                              className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 rounded-lg flex items-center justify-center cursor-pointer"
                              onClick={() => handleImageClick(item.content, item.title || 'Shared image')}
                            >
                              <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-white bg-opacity-90 rounded-full p-2">
                                <ZoomIn className="w-6 h-6 text-gray-700" />
                              </div>
                            </div>
                          </div>
                          <div 
                            className="hidden w-full h-64 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 text-sm"
                          >
                            <div className="text-center">
                              <Eye className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                              <p>Image not available</p>
                            </div>
                          </div>
                        </div>
                      ) : item.content_type === 'link' && item.content.match(/^https?:\/\//) ? (
                        <div className="space-y-3">
                          <div className="text-sm">
                            <a
                              href={item.content}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 underline break-all"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {item.content}
                            </a>
                          </div>
                          <LinkPreview url={item.content} />
                        </div>
                      ) : (
                        item.content
                      )}
                    </div>
                    
                    {/* Tags */}
                    {item.tags && Array.isArray(item.tags) && item.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-3">
                        {item.tags.slice(0, 3).map((tag, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-indigo-100 text-indigo-800"
                          >
                            <Hash className="w-3 h-3 mr-1" />
                            {tag}
                          </span>
                        ))}
                        {item.tags.length > 3 && (
                          <span className="text-xs text-gray-500">
                            +{item.tags.length - 3} more
                          </span>
                        )}
                      </div>
                    )}

                    {/* Inline Category Editor */}
                    {editingItem === item.id && (
                      <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex items-center space-x-2">
                          <select
                            value={editCategory}
                            onChange={(e) => setEditCategory(e.target.value)}
                            className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                            disabled={categoriesLoading}
                          >
                            <option value="">Select a category...</option>
                            {categories.map((category) => (
                              <option key={category.id} value={category.id}>
                                {category.name}
                              </option>
                            ))}
                          </select>
                          <button
                            onClick={() => handleSaveCategory(item.id)}
                            disabled={!editCategory || categoriesLoading}
                            className="px-3 py-1.5 bg-indigo-600 text-white text-sm rounded-md hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Save
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            className="px-3 py-1.5 bg-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-400 transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Comments Footer */}
                  <div className="px-4 py-3 bg-gray-50 border-t border-gray-100">
                    {/* Comments Summary Toggle */}
                    <button
                      onClick={() => setCommentsExpanded(prev => ({ ...prev, [item.id]: !prev[item.id] }))}
                      className="w-full flex items-center justify-between text-xs text-gray-500 hover:text-gray-700 transition-colors"
                    >
                      <div className="flex items-center space-x-4">
                        <span className="flex items-center space-x-1">
                          <MessageCircle className="w-3 h-3" />
                          <span>{commentCounts[item.id] || 0} comments</span>
                        </span>
                        {commentCounts[item.id] > 0 && commentData[item.id] && commentData[item.id].length > 0 && (
                          <span className="flex items-center space-x-1">
                            <Clock className="w-3 h-3" />
                            <span>Latest: {new Date(commentData[item.id][0]?.created_at || Date.now()).toLocaleDateString()}</span>
                          </span>
                        )}
                      </div>
                      <div className="flex items-center space-x-1">
                        <span className="text-xs text-gray-500 group-hover:text-blue-500 transition-colors duration-200">
                          {commentsExpanded[item.id] ? 'Click to hide' : (commentCounts[item.id] || 0) === 0 ? 'Be the first to comment!' : 'Click to view and comment'}
                        </span>
                      </div>
                    </button>
                    
                    {/* Comments Section - Always render to fetch data, but conditionally show content */}
                    <div className={commentsExpanded[item.id] ? 'mt-3' : 'hidden'}>
                      <CommentSection
                        itemId={item.id}
                        initialComments={item.comments || []}
                        hideToggle={true}
                        onCommentCountUpdate={handleCommentCountUpdate}
                      />
                    </div>
                  </div>
                </div>
              ))}
              
              {/* Infinite scroll loading indicator */}
              {hasMore && (
                <div className="col-span-full text-center py-4">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto text-indigo-600" />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Items count info */}
        {items.length > 0 && (
          <div className="flex justify-center mt-8">
            <div className="text-sm text-gray-600">
              Showing {items.length} of {pagination.total_items || items.length} items
            </div>
          </div>
        )}
      </div>

      {/* Image Modal */}
      <ImageModal
        isOpen={imageModal.isOpen}
        imageSrc={imageModal.imageSrc}
        imageAlt={imageModal.imageAlt}
        onClose={handleCloseImageModal}
      />
    </div>
  );
};

export default PublicCategoryPage; 